import { omit } from "lodash";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { sunoCommsApi } from "../api-base";
import {
  CallLocation,
  CallLocationFilter,
  CallLocationInput
} from "../types/call-location.type";
import {
  LocationSettings,
  LocationSettingsFilter,
  LocationSettingsInput
} from "../types/location-settings.type";
import {
  UserSettings,
  UserSettingsFilter,
  UserSettingsInput
} from "../types/user-settings.type";

export const locationsEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getLocations: builder.query<CallLocation[], CallLocationFilter>({
      query: (filter) =>
        `/locations/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["CallLocations"]
    }),
    getUserSettings: builder.query<UserSettings, UserSettingsFilter>({
      query: (filter) =>
        `/userSettings/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["CallUserSettings"]
    }),
    updateUserSettings: builder.mutation<
      UserSettings,
      Partial<UserSettingsInput> & { id: string }
    >({
      query: (data) => ({
        url: `/userSettings/${data.id}`,
        method: "PATCH",
        body: omit(data, "id")
      }),
      invalidatesTags: ["CallUserSettings"]
    }),
    getLocationSettings: builder.query<
      LocationSettings,
      LocationSettingsFilter
    >({
      query: (filter) =>
        `/locationSettings/${formatValidQueryParamUrlCommaSeparated(
          filter,
          true
        )}`,
      providesTags: ["CallLocationSettings"]
    }),
    createLocation: builder.mutation<CallLocationInput, CallLocation>({
      query: (body) => ({
        url: `/locations/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["CallLocations"]
    }),
    updateLocation: builder.mutation<
      Partial<Omit<CallLocationInput, "id">> & Pick<CallLocationInput, "id">,
      CallLocation
    >({
      query: (data) => ({
        url: `/locations/${data.id}`,
        method: "PATCH",
        body: omit(data, "id")
      }),
      invalidatesTags: ["CallLocations"]
    }),
    updateLocationSettings: builder.mutation<
      Partial<Omit<LocationSettingsInput, "id">> &
        Pick<LocationSettingsInput, "id">,
      LocationSettingsInput
    >({
      query: (data) => ({
        url: `/locationSettings/${data.id}`,
        method: "PATCH",
        body: omit(data, "id")
      }),
      invalidatesTags: ["CallLocationSettings"]
    })
  })
});

export const {
  useGetLocationsQuery,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  useGetLocationSettingsQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useUpdateLocationSettingsMutation
} = locationsEndpoints;
